import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminSwal as Swal } from "./adminSwal";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { adminInstituicoesApi } from "../../../api/adminInstituicoes";
import "./AdminInstituicoes.scss";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

export default function AdminInstituicoes() {
  const navigate = useNavigate();
  const [busca, setBusca] = useState("");
  const [ativa, setAtiva] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (busca) params.busca = busca;
      if (ativa !== "") params.ativa = ativa === "true";
      setData(await adminInstituicoesApi.listar(params));
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao listar", "error");
    } finally {
      setLoading(false);
    }
  }, [busca, ativa, page, pageSize]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const onCriar = async () => {
    const { value, isConfirmed } = await Swal.fire({
      title: "Nova instituição",
      html: `
        <input id="sw-nome" class="swal2-input" placeholder="Nome" />
        <input id="sw-sigla" class="swal2-input" placeholder="Sigla (ex: UCB)" maxlength="20" />
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Criar",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const nome = document.getElementById("sw-nome").value.trim();
        const sigla = document.getElementById("sw-sigla").value.trim();
        if (!nome || !sigla) {
          Swal.showValidationMessage("Nome e sigla são obrigatórios");
          return false;
        }
        return { nome, sigla };
      },
    });
    if (!isConfirmed) return;
    try {
      await adminInstituicoesApi.criar(value);
      Swal.fire("OK", "Instituição criada", "success");
      carregar();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha", "error");
    }
  };

  const onEditar = useCallback(
    async (i) => {
      const { value, isConfirmed } = await Swal.fire({
        title: `Editar ${i.nome}`,
        html: `
        <input id="sw-nome" class="swal2-input" value="${i.nome}" placeholder="Nome" />
        <input id="sw-sigla" class="swal2-input" value="${i.sigla}" maxlength="20" placeholder="Sigla" />
        <textarea id="sw-desc" class="swal2-textarea" placeholder="Descrição (opcional)" maxlength="1000">${i.descricao || ""}</textarea>
        <input id="sw-logo" type="file" accept="image/*" class="swal2-file" />
      `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Salvar",
        preConfirm: () => ({
          nome: document.getElementById("sw-nome").value.trim(),
          sigla: document.getElementById("sw-sigla").value.trim(),
          descricao: document.getElementById("sw-desc").value.trim() || null,
          logoFile: document.getElementById("sw-logo").files[0] || null,
        }),
      });
      if (!isConfirmed) return;
      try {
        await adminInstituicoesApi.editar(i.id, {
          nome: value.nome,
          sigla: value.sigla,
          descricao: value.descricao,
          ativa: i.ativa,
        });
        if (value.logoFile) {
          await adminInstituicoesApi.uploadLogo(i.id, value.logoFile);
        }
        Swal.fire("OK", "Instituição atualizada", "success");
        carregar();
      } catch (err) {
        Swal.fire("Erro", err?.message || "Falha", "error");
      }
    },
    [carregar],
  );

  const onExcluir = useCallback(
    async (i) => {
      const step1 = await Swal.fire({
        title: `Excluir ${i.nome}?`,
        text: "Essa ação é irreversível. Deseja continuar?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, continuar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#d33",
      });
      if (!step1.isConfirmed) return;

      const step2 = await Swal.fire({
        title: "Confirmação final",
        html: `<p>Todos os dados da instituição <strong>${i.nome}</strong> serão excluídos permanentemente — usuários, turmas, coordenações e tudo mais.</p><p><strong>Essa ação não pode ser desfeita.</strong></p>`,
        icon: "error",
        showCancelButton: true,
        confirmButtonText: "Excluir definitivamente",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#d33",
      });
      if (!step2.isConfirmed) return;

      try {
        await adminInstituicoesApi.excluir(i.id);
        Swal.fire("Excluída", "Instituição removida permanentemente.", "success");
        carregar();
      } catch (err) {
        Swal.fire("Erro", err?.message || "Falha ao excluir", "error");
      }
    },
    [carregar],
  );

  const onToggle = useCallback(
    async (i) => {
      const acao = i.ativa ? "desativar" : "reativar";
      const ok = await Swal.fire({
        title: `${acao[0].toUpperCase() + acao.slice(1)} ${i.nome}?`,
        icon: "warning",
        showCancelButton: true,
      });
      if (!ok.isConfirmed) return;
      try {
        await adminInstituicoesApi.editar(i.id, {
          nome: i.nome,
          sigla: i.sigla,
          ativa: !i.ativa,
        });
        carregar();
      } catch (err) {
        Swal.fire("Erro", err?.message, "error");
      }
    },
    [carregar],
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "logoUrl",
        header: "",
        cell: (info) => {
          const url = info.getValue();
          return url ? (
            <img src={`${API_BASE}${url}`} alt="" className="logo-thumb" />
          ) : null;
        },
      },
      { accessorKey: "nome", header: "Nome" },
      { accessorKey: "sigla", header: "Sigla" },
      {
        accessorKey: "ativa",
        header: "Status",
        cell: (i) => (
          <span
            className={`status-badge ${i.getValue() ? "ativa" : "inativa"}`}
          >
            {i.getValue() ? "Ativa" : "Inativa"}
          </span>
        ),
      },
      {
        id: "acoes",
        header: "Ações",
        cell: ({ row }) => {
          const i = row.original;
          return (
            <div className="acoes">
              <button onClick={() => navigate(`/admin/instituicoes/${i.id}`)}>
                Gerenciar
              </button>
              <button onClick={() => onEditar(i)}>Editar</button>
              {!i.protegida && (
                <button onClick={() => onToggle(i)}>
                  {i.ativa ? "Desativar" : "Reativar"}
                </button>
              )}
              {!i.protegida && !i.ativa && (
                <button className="danger" onClick={() => onExcluir(i)}>
                  Excluir
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [navigate, onEditar, onToggle, onExcluir],
  );

  const table = useReactTable({
    data: data.items || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  return (
    <div className="admin-instituicoes">
      <header>
        <h1>Instituições</h1>
        <button className="primary" onClick={onCriar}>
          Nova instituição
        </button>
      </header>

      <section className="filtros">
        <input
          type="search"
          placeholder="Buscar nome ou sigla"
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value);
            setPage(1);
          }}
        />
        <select
          value={ativa}
          onChange={(e) => {
            setAtiva(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todas</option>
          <option value="true">Apenas ativas</option>
          <option value="false">Apenas inativas</option>
        </select>
      </section>

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <table className="tabela">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="vazio">
                  Nenhuma instituição.
                </td>
              </tr>
            )}
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <footer className="paginacao">
        <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
          Anterior
        </button>
        <span>
          Página {page} de {totalPages} • Total: {data.total}
        </span>
        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Próxima
        </button>
      </footer>
    </div>
  );
}
